// Hook principal para gestionar lista de expedientes
// Mobile-first con infinite scroll, caché React Query y prefetch

import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { expedienteApi } from '@/services/expedienteApi';
import type {
  ExpedienteRead,
  ExpedienteFilters,
  ExpedienteListResponse,
} from '@/types/expediente';

/** Clave de query para lista de expedientes (para prefetch e invalidación) */
export function expedientesListQueryKey(filters: ExpedienteFilters = {}, pageSize = 20) {
  const key = JSON.stringify(filters);
  return ['expedientes', key, pageSize] as const;
}

const PAGE_SIZE_DEFAULT = 20;
const STALE_TIME_MS = 60_000; // 1 min: repeat visits usan caché

interface UseExpedientesOptions {
  filters?: ExpedienteFilters;
  autoLoad?: boolean;
  pageSize?: number;
}

export function useExpedientes(options: UseExpedientesOptions = {}) {
  const { filters = {}, autoLoad = true, pageSize = PAGE_SIZE_DEFAULT } = options;
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => expedientesListQueryKey(filters, pageSize),
    [filters, pageSize]
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const response: ExpedienteListResponse = await expedienteApi.list({
        ...filters,
        skip: pageParam as number,
        limit: pageSize,
      });
      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextSkip = lastPage.skip + lastPage.items.length;
      if (lastPage.items.length < pageSize || nextSkip >= lastPage.total) return undefined;
      return nextSkip;
    },
    staleTime: STALE_TIME_MS,
    retry: 1,
    enabled: autoLoad,
  });

  const expedientes = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data?.pages]
  );

  const total = data?.pages[0]?.total ?? 0;

  const loadMore = useMemo(() => {
    return () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const refresh = useMemo(() => () => refetch(), [refetch]);

  const removeExpediente = (id: string) => {
    queryClient.setQueryData(
      queryKey,
      (old: { pages: ExpedienteListResponse[]; pageParams?: unknown[] } | undefined) => {
        if (!old?.pages?.length) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((e) => e.id !== id),
            total: page.skip === 0 ? Math.max(0, page.total - 1) : page.total,
          })),
        };
      }
    );
  };

  const updateExpediente = (updated: ExpedienteRead) => {
    queryClient.setQueryData(
      queryKey,
      (old: { pages: ExpedienteListResponse[]; pageParams?: unknown[] } | undefined) => {
        if (!old?.pages?.length) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((e) => (e.id === updated.id ? updated : e)),
          })),
        };
      }
    );
  };

  return {
    expedientes,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    error: error ? (error instanceof Error ? error : new Error('Error al cargar expedientes')) : null,
    hasMore: hasNextPage ?? false,
    total,
    loadMore,
    refresh,
    removeExpediente,
    updateExpediente,
  };
}
